// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the phone rail's vocabulary. The rail is a floating glass pill of
// icon-only tabs plus the dark Ask pill. Users choose up to five tabs in
// Settings > Mobile rail; Ask is always there and never counts.

export const MOBILE_RAIL_TAB_IDS = [
	'home',
	'chats',
	'mail',
	'more',
	'calendar',
	'scratchpad',
	'reminders',
	'whatsapp',
	'clickup',
	'agents',
	'scheduled',
] as const;

export type MobileRailTabId = (typeof MOBILE_RAIL_TAB_IDS)[number];

export const MOBILE_RAIL_MAX_TABS = 5;

export const DEFAULT_MOBILE_RAIL_TABS: ReadonlyArray<MobileRailTabId> = ['home', 'chats', 'mail', 'more'];

// The More panel is a deliberately small quick menu. Keep its catalogue in
// the order that gives the default four tiles (the server's default too), then
// offer the remaining destinations when one of those is already on the rail.
export const MOBILE_MORE_APP_IDS = [
	'calendar',
	'clickup',
	'scratchpad',
	'scheduled',
	'reminders',
	'chats',
	'mail',
	'whatsapp',
	'agents',
] as const satisfies ReadonlyArray<MobileRailTabId>;

export type MobileMoreAppId = (typeof MOBILE_MORE_APP_IDS)[number];

export const MOBILE_MORE_MAX_APPS = 4;

export function isMobileRailTabId(value: unknown): value is MobileRailTabId {
	return typeof value === 'string' && (MOBILE_RAIL_TAB_IDS as ReadonlyArray<string>).includes(value);
}

/** Drops unknown and duplicate ids, caps at the max, falls back to the default when empty. */
export function sanitizeMobileRailTabs(raw: unknown): Array<MobileRailTabId> {
	if (!Array.isArray(raw)) return [...DEFAULT_MOBILE_RAIL_TABS];
	const seen = new Set<MobileRailTabId>();
	const tabs: Array<MobileRailTabId> = [];
	for (const id of raw) {
		if (!isMobileRailTabId(id) || seen.has(id)) continue;
		seen.add(id);
		tabs.push(id);
		if (tabs.length >= MOBILE_RAIL_MAX_TABS) break;
	}
	return tabs.length > 0 ? tabs : [...DEFAULT_MOBILE_RAIL_TABS];
}

export function isMobileMoreAppId(value: unknown): value is MobileMoreAppId {
	return typeof value === 'string' && (MOBILE_MORE_APP_IDS as ReadonlyArray<string>).includes(value);
}

/** The legacy More menu, completed with off-rail apps so no slot is wasted on a duplicate. */
export function defaultMobileMoreApps(tabs: ReadonlyArray<MobileRailTabId>): Array<MobileMoreAppId> {
	return MOBILE_MORE_APP_IDS.filter((id) => !tabs.includes(id)).slice(0, MOBILE_MORE_MAX_APPS);
}

/** Drops unknown, duplicate and on-rail apps, and caps the More quick menu at four tiles. */
export function sanitizeMobileMoreApps(raw: unknown, tabs: ReadonlyArray<MobileRailTabId>): Array<MobileMoreAppId> {
	if (!Array.isArray(raw)) return defaultMobileMoreApps(tabs);
	const seen = new Set<MobileMoreAppId>();
	const apps: Array<MobileMoreAppId> = [];
	for (const id of raw) {
		if (!isMobileMoreAppId(id) || seen.has(id) || tabs.includes(id)) continue;
		seen.add(id);
		apps.push(id);
		if (apps.length >= MOBILE_MORE_MAX_APPS) break;
	}
	return apps;
}

/** The More tiles everyone had before Agents and Scheduled existed (the server's old default). */
const LEGACY_DEFAULT_MOBILE_MORE_APPS: ReadonlyArray<MobileMoreAppId> = [
	'calendar',
	'clickup',
	'scratchpad',
	'reminders',
];

/** The server's default since the Scheduled page: Scheduled (which lists reminders too) in Reminders' place. */
const DEFAULT_MOBILE_MORE_APPS: ReadonlyArray<MobileMoreAppId> = ['calendar', 'clickup', 'scratchpad', 'scheduled'];

/** What people with agents access get in place of untouched default tiles (the legacy or the current ones). */
const AGENTS_DEFAULT_MOBILE_MORE_APPS: ReadonlyArray<MobileMoreAppId> = ['calendar', 'clickup', 'agents', 'scratchpad'];

/**
 * KONVO: Agents only exists for people with agents access, so the rail drops its
 * tab for everyone else (the saved choice is kept for when access comes back).
 */
export function visibleMobileRailTabs(
	tabs: ReadonlyArray<MobileRailTabId>,
	agentsEnabled: boolean,
): Array<MobileRailTabId> {
	return agentsEnabled ? [...tabs] : tabs.filter((id) => id !== 'agents');
}

/**
 * The More tiles to draw. Without agents access the Agents tile is dropped. With it,
 * someone still on untouched default tiles sees Agents in place of Reminders, so
 * the new page is one tap away without anyone having to visit Settings. Otherwise
 * the untouched legacy tiles show Scheduled in place of Reminders, like the server's
 * new default. A choice the user made themselves is left alone, Reminders included.
 */
export function effectiveMobileMoreApps(
	apps: ReadonlyArray<MobileMoreAppId>,
	tabs: ReadonlyArray<MobileRailTabId>,
	agentsEnabled: boolean,
): Array<MobileMoreAppId> {
	const legacy = sameMobileMoreApps(apps, LEGACY_DEFAULT_MOBILE_MORE_APPS);
	const untouched = legacy || sameMobileMoreApps(apps, DEFAULT_MOBILE_MORE_APPS);
	if (agentsEnabled && untouched && !tabs.includes('agents')) {
		return AGENTS_DEFAULT_MOBILE_MORE_APPS.filter((id) => !tabs.includes(id));
	}
	if (legacy && !tabs.includes('scheduled')) return [...DEFAULT_MOBILE_MORE_APPS];
	return agentsEnabled ? [...apps] : apps.filter((id) => id !== 'agents');
}

function sameMobileMoreApps(a: ReadonlyArray<MobileMoreAppId>, b: ReadonlyArray<MobileMoreAppId>): boolean {
	return a.length === b.length && a.every((id, index) => id === b[index]);
}
